/**
 * Utility functions for saving and loading configurations to/from the database
 */

import db from './database';
import { extractConfig, validateConfig } from './configExport';

/**
 * Save a configuration to the database
 * @param {Object} state - Current application state
 * @param {string} configName - Name for the configuration
 * @param {string} description - Optional description for the configuration
 * @returns {Promise<Object>} Result of the save operation
 */
export const saveConfigToDatabase = async (state, configName, description = '') => {
  if (!configName || configName.trim() === '') {
    throw new Error('Configuration name is required');
  }

  try {
    // Extract the current configuration
    const configData = extractConfig(state);

    // Check if table exists, create it if not
    await ensureConfigTableExists();

    // Check if configuration with this name already exists
    const existingConfig = await getConfigByName(configName);
    
    if (existingConfig) {
      // Update existing configuration
      const updateQuery = `
        UPDATE configurations 
        SET config_data = ?, 
            description = ?, 
            updated_at = ?
        WHERE name = ?
      `;
      
      await db.query(updateQuery, [
        JSON.stringify(configData),
        description,
        new Date().toISOString(),
        configName
      ]);
      
      return { success: true, message: `Configuration '${configName}' updated successfully` };
    } else {
      // Insert new configuration
      const insertQuery = `
        INSERT INTO configurations (name, description, config_data, created_at, updated_at)
        VALUES (?, ?, ?, ?, ?)
      `;
      
      const timestamp = new Date().toISOString();
      
      await db.query(insertQuery, [
        configName,
        description,
        JSON.stringify(configData),
        timestamp,
        timestamp
      ]);
      
      return { success: true, message: `Configuration '${configName}' saved successfully` };
    }
  } catch (error) {
    console.error('Error saving configuration to database:', error);
    throw error;
  }
};

/**
 * Load a configuration from the database
 * @param {string} configName - Name of the configuration to load
 * @returns {Promise<Object>} Configuration object
 */
export const loadConfigFromDatabase = async (configName) => {
  try {
    const config = await getConfigByName(configName);
    
    if (!config) {
      throw new Error(`Configuration '${configName}' not found`);
    }
    
    // Parse the configuration data
    const configData = JSON.parse(config.config_data);
    
    // Validate the configuration
    const validation = validateConfig(configData);
    if (!validation.valid) {
      throw new Error(`Invalid configuration: ${validation.message}`);
    }
    
    return configData;
  } catch (error) {
    console.error('Error loading configuration from database:', error);
    throw error;
  }
};

/**
 * Delete a configuration from the database
 * @param {string} configName - Name of the configuration to delete
 * @returns {Promise<Object>} Result of the delete operation
 */
export const deleteConfigFromDatabase = async (configName) => {
  try {
    // Check if configuration exists
    const config = await getConfigByName(configName);
    
    if (!config) {
      throw new Error(`Configuration '${configName}' not found`);
    }
    
    // Delete configuration
    const deleteQuery = `DELETE FROM configurations WHERE name = ?`;
    await db.query(deleteQuery, [configName]);
    
    return { success: true, message: `Configuration '${configName}' deleted successfully` };
  } catch (error) {
    console.error('Error deleting configuration from database:', error);
    throw error;
  }
};

/**
 * Get all saved configurations from the database
 * @returns {Promise<Array>} Array of configuration objects
 */
export const getAllConfigurations = async () => {
  try {
    await ensureConfigTableExists();
    
    const query = `
      SELECT name, description, updated_at
      FROM configurations
      ORDER BY updated_at DESC
    `;
    
    const result = await db.query(query);
    return result.rows || [];
  } catch (error) {
    console.error('Error getting configurations from database:', error);
    throw error;
  }
};

/**
 * Get a configuration by name
 * @param {string} configName - Name of the configuration to get
 * @returns {Promise<Object>} Configuration object or null if not found
 */
export const getConfigByName = async (configName) => {
  try {
    await ensureConfigTableExists();
    
    const query = `SELECT * FROM configurations WHERE name = ?`;
    const result = await db.query(query, [configName]);
    
    return result.rows && result.rows.length > 0 ? result.rows[0] : null;
  } catch (error) {
    console.error('Error getting configuration by name:', error);
    throw error;
  }
};

/**
 * Ensure the configurations table exists in the database
 * @returns {Promise<void>}
 */
export const ensureConfigTableExists = async () => {
  try {
    // Check if table exists
    const tableExistsQuery = `
      SELECT name FROM sqlite_master 
      WHERE type='table' AND name='configurations'
    `;
    
    const result = await db.query(tableExistsQuery);
    
    if (!result.rows || result.rows.length === 0) {
      // Create table if it doesn't exist
      const createTableQuery = `
        CREATE TABLE configurations (
          id INTEGER PRIMARY KEY AUTOINCREMENT,
          name TEXT NOT NULL UNIQUE,
          description TEXT,
          config_data TEXT NOT NULL,
          created_at TEXT NOT NULL,
          updated_at TEXT NOT NULL
        )
      `;
      
      await db.query(createTableQuery);
    }
  } catch (error) {
    console.error('Error ensuring configurations table exists:', error);
    throw error;
  }
};